import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { IItemsListDataFetchResult } from '@fi-shared/fi-items-list/fi-items-list';
import { IInsertMediaUploadItem } from '@fi-shared/fi-insert-media/fi-insert-media';
import { Observable } from 'rxjs';
import { FiRemoteDataSource } from '@fi-shared/fi-items-list/fi-remote-data-source';
import { takeUntil } from 'rxjs/operators';
import { MediaBankListParameter } from '@fi-shared/media-bank/media-bank-list-parameter';
import { fiGetMediaBankFileIconAwesome } from '@fi-shared/utils';
import { MediaBankHelperService } from '@fi-shared/media-bank/media-bank-helper.service';
import { FiMediaBankService, IMediaBankFile } from '@fi-shared/media-bank';
import { MatSort } from '@angular/material/sort';
import { FiMediaBankFileModel } from '@fi-shared/models';
import { FileType } from '@fi-shared/types';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';

@Component({
    selector: 'fi-insert-media-details-old-uploads',
    templateUrl: './fi-insert-media-details-old-uploads.component.html',
    styleUrls: ['./fi-insert-media-details-old-uploads.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-insert-media-details-old-uploads' },
    standalone: false,
})
export class FiInsertMediaDetailsOldUploadsComponent
    extends FiRemoteDataSource<IInsertMediaUploadItem>
    implements OnInit
{
    @Input() uploadStarted$ = new Observable<IInsertMediaUploadItem>();
    @Input() uploadCancelled$ = new Observable<IInsertMediaUploadItem>();
    @Input() uploadFinished$ = new Observable<IInsertMediaUploadItem>();
    @Input() allowStoreInMyFiles: boolean;

    @Input() filterTypes: number[] = [];
    @Input() filterTypePeriods: number[] = [];
    @Input() selectedTagIds: number[] = [];
    @Input() reloadData$ = new Observable<void>();
    @Input() multiple: boolean;
    @Input() allowedFileTypes: FileType[];
    @Input() isTableDisplayType: boolean;
    @Input() isMobilePhone: boolean;
    @Output() selectedUploadItemsChange = new EventEmitter<IInsertMediaUploadItem[]>();
    @ViewChild('sort', { static: true }) sort: MatSort;

    selectedUploadItems: IInsertMediaUploadItem[] = [];
    uploads: IInsertMediaUploadItem[] = [];
    allUploadItems: IInsertMediaUploadItem[] = [];

    isCheckboxItemDisabledFn = (item: IInsertMediaUploadItem) => {
        return item.percentageLoaded$ && item.percentageLoaded$.value < 100;
    };

    constructor(injector: Injector) {
        super(injector);
    }

    ngOnInit() {
        this.setServices();
        this.subscribeOnDataReload();
        this.subscribeOnUploadActions();

        this.items$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.updateAllUploadItems();
        });
    }

    private subscribeOnDataReload() {
        this.reloadData$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => this.reloadData());
    }

    private subscribeOnUploadActions() {
        this.uploadStarted$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((uploadItem) => {
            this.uploads.push(uploadItem);
            this.updateAllUploadItems();
        });
        this.uploadCancelled$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((uploadItem) => {
            this.uploads.splice(this.uploads.indexOf(uploadItem), 1);
            this.updateAllUploadItems();
        });
        this.uploadFinished$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((uploadItem) => {
            this.selectedUploadItems = this.selectedUploadItems.concat([uploadItem]).slice();
        });
    }

    onSelectionChanged($event: IInsertMediaUploadItem[]) {
        this.selectedUploadItems = $event.slice();
        this.selectedUploadItemsChange.emit($event);
    }

    loadListDataFn(loadArgs: ILoadDataArgs): Promise<IItemsListDataFetchResult<IInsertMediaUploadItem>> {
        const parameter = this.getMediaBankFileParameter();
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            //FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: true,
        };
        parameter.Offset = loadArgs.fetchParams.offset;
        parameter.NumberOfRecordsToTake = loadArgs.fetchParams.numberOfRecordsToTake;
        if (this.allowedFileTypes?.length) {
            parameter.FileTypes = this.allowedFileTypes;
        }
        return new Promise<IItemsListDataFetchResult<IInsertMediaUploadItem>>((resolve, reject) => {
            this.fiMediaBankService.getMediaBankFilesListData(parameter).then((response) => {
                resolve({
                    Items: response.Items.map((item) => ({
                        mediaBankFile: new FiMediaBankFileModel(this.fiFormatDateService, item),
                    })),
                    TotalCount: response.TotalCount,
                    LightWeightObjectsForLocalSearch: response.LightWeightObjectsForLocalSearch,
                });
            }, reject);
        });
    }

    private getMediaBankFileParameter() {
        const parameter = new MediaBankListParameter();
        parameter.SelectedSort = this.getSelectedSort();
        parameter.TagIds = this.selectedTagIds;
        parameter.FilterTypes = this.filterTypes.concat(this.filterTypePeriods);
        this.mergeLocalSearchParameter(parameter);
        return parameter;
    }

    getMediaBankFileSize(mediaBank: IMediaBankFile): string {
        return this.mediaBankHelperService.getFileSizeText(mediaBank.Size);
    }

    getImage(item: IInsertMediaUploadItem) {
        return this.mediaBankHelperService.getMediaFileUrlPreview(item);
    }

    getTagNames(item: IMediaBankFile): string {
        return item.Tags.map((tag) => tag.Name).join(', ');
    }

    getIconClass(item: IInsertMediaUploadItem): string {
        return fiGetMediaBankFileIconAwesome(item.mediaBankFile.FileType);
    }

    getSizeAndChangedOnData(item: IInsertMediaUploadItem): string {
        return `${this.getMediaBankFileSize(item.mediaBankFile)}, ${this.fiFormatDateService.formatDate(
            item.mediaBankFile.ChangedOn,
            'date',
        )}`;
    }

    private mediaBankHelperService: MediaBankHelperService;
    private fiMediaBankService: FiMediaBankService;
    private cdr: ChangeDetectorRef;
    private fiFormatDateService: FiFormatDateService;

    private setServices() {
        this.mediaBankHelperService = this.injector.get(MediaBankHelperService);
        this.fiMediaBankService = this.injector.get(FiMediaBankService);
        this.cdr = this.injector.get(ChangeDetectorRef);
        this.fiFormatDateService = this.injector.get(FiFormatDateService);
    }

    ngAfterViewInit(): void {
        this.reloadData();
    }

    onAbortUploadClick($event: MouseEvent, item: IInsertMediaUploadItem) {
        $event.stopPropagation();
        item.abortUpload();
    }

    removeNewUpload(item: IInsertMediaUploadItem) {
        this.uploads.splice(this.uploads.indexOf(item), 1);
        this.updateAllUploadItems();
    }

    private updateAllUploadItems() {
        this.allUploadItems = this.uploads.concat(this.items());
        this.selectedUploadItems = this.selectedUploadItems
            .filter((uploadItem) => this.allUploadItems.indexOf(uploadItem) !== -1)
            .slice();
        this.cdr.markForCheck();
    }
}
